import { useCallback, useEffect, useRef, useState } from 'react';
import type { WarningConfiguration } from '../../consumer/configuration';
import type { Acknowledgement, WarningDetail, WarningRevision } from '../../consumer/contract';
import { riskHref, type RiskPageProps } from '../../consumer/workbench';
import './details.css';
import {
  DetailReadNotice,
  Fields,
  detailFailure,
  metricLabel,
  refs,
  useDetailRead,
} from './shared';

/** Select support strictly from the archived revision, never union it with newer evidence. */
export function warningRevisionEvidence(data: WarningDetail, revision: WarningRevision) {
  const references = new Set([
    ...revision.supportObservationRefs,
    ...revision.conflictObservationRefs,
  ]);
  return {
    observations: data.observations.filter((o) => references.has(o.reference)),
    detections: data.detections.filter(
      (d) => d.observationRefs.length > 0 && d.observationRefs.every((r) => references.has(r)),
    ),
    missingReferences: [...references].filter(
      (r) => !data.observations.some((o) => o.reference === r),
    ),
  };
}
export function WarningRevisionEvidence({
  data,
  revision,
  configuration,
}: {
  readonly data: WarningDetail;
  readonly revision: WarningRevision;
  readonly configuration: WarningConfiguration | null;
}) {
  const evidence = warningRevisionEvidence(data, revision);
  const basis = configuration?.revisions.find((r) => r.revision === revision.revision);
  return (
    <>
      <h3>观测与关联依据</h3>
      <p className="nm-vs04-note">
        以下是第 {revision.revision}{' '}
        版预警保存的原始支持与冲突引用。后续观测及修订不会替换这份历史依据。
      </p>
      {!evidence.observations.length && <p>本修订没有可查询的原始观测。依据覆盖未知。</p>}
      {evidence.missingReferences.length > 0 && (
        <p role="status">原始观测缺失：{refs(evidence.missingReferences)}；无法核验这部分依据。</p>
      )}
      {evidence.observations.length > 0 && (
        <div className="nm-vs04-table-wrap">
          <table className="nm-vs04-table">
            <thead>
              <tr>
                <th>来源与原始引用</th>
                <th>指标与实际观测</th>
                <th>观测 / 接收时间</th>
                <th>质量与资格</th>
              </tr>
            </thead>
            <tbody>
              {evidence.observations.map((o) => {
                const b = configuration?.observations.find(
                  (entry) => entry.reference === o.reference,
                );
                return (
                  <tr key={o.reference} data-nm-observation={o.reference}>
                    <td>
                      {o.input.sourceId}
                      <small>{o.reference}</small>
                      <small>
                        {revision.supportObservationRefs.includes(o.reference)
                          ? '支持依据'
                          : '冲突依据'}
                      </small>
                      <small>原始引用 {o.input.rawReference}</small>
                      <small>
                        来源版本 {o.input.sourceVersion} / 适配器 {o.input.adapterVersion}
                      </small>
                    </td>
                    <td>
                      {metricLabel(o.input.metric)}
                      <small>
                        {o.input.value} {o.input.unit}
                      </small>
                      {o.input.metric === 'THERMAL_SCORE' && <small>SIMULATED_MODEL_OUTPUT</small>}
                    </td>
                    <td>
                      {o.input.observedAt}
                      <small>接收 {o.receivedAt}</small>
                    </td>
                    <td>
                      {o.input.quality} · {o.input.synthetic ? '合成' : '来源声明非合成'}
                      <small>Unknown：{refs(o.input.unknowns)}</small>
                      <small>
                        {o.processingStatus} · {o.processingReason}
                      </small>
                      <small>
                        配置 {b?.configRevision ?? '未知'} / 来源 {b?.sourceRevision ?? '未知'} /
                        epoch {b?.epoch ?? '未知'}
                      </small>
                      <small>{b?.basis ?? '配置依据不可用'}</small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <details className="nm-vs04-section">
        <summary>本修订规则与基线检测</summary>
        {!evidence.detections.length && (
          <p>无能够完整归属本修订引用集合的检测记录。不得用其他修订的检测替代。</p>
        )}
        {evidence.detections.map((d) => (
          <article
            key={d.detectionId}
            className="nm-vs04-evidence"
            data-nm-detection={d.detectionId}
          >
            <strong>
              {d.sourceId} · {d.result}
            </strong>
            <p>
              {d.windowStart} → {d.windowEnd}
            </p>
            <p>
              基线 {d.baseline} · 偏移 {d.deviation} · 连续异常 {d.consecutiveAnomalies} / 正常{' '}
              {d.consecutiveNormals}
            </p>
            <p>
              {d.algorithmVersion} · {d.detectionId}
            </p>
            <p>原始引用：{refs(d.observationRefs)}</p>
            <p>局限：{refs(d.limitations)}</p>
          </article>
        ))}
      </details>
      <details className="nm-vs04-section" data-nm-warning-configuration>
        <summary>本修订配置与恢复集合</summary>
        {basis ? (
          <div data-nm-basis-revision={basis.revision}>
            <Fields
              items={[
                ['配置修订', basis.configRevision],
                ['配置依据', basis.basis],
                ['历史必须来源', refs(basis.requiredSourceIds)],
                ['缺失来源', refs(basis.missingSourceIds)],
                ['缺失指标', refs(basis.missingMetrics)],
                ['变化类型', basis.changeKind],
              ]}
            />
          </div>
        ) : (
          <p>本修订配置依据未知；尚不可将当前配置解释为历史配置。</p>
        )}
        <p className="nm-vs04-muted">
          停用或新增来源不会缩小本 episode 的历史恢复集合；缺口不会自动解除预警。LEGACY_VS01
          表示旧记录没有当时的配置审计。
        </p>
      </details>
    </>
  );
}
export function WarningDetailPage({
  id,
  revision,
  port,
  configurationPort,
  areaId,
}: RiskPageProps & { readonly id: string; readonly revision?: number }) {
  const read = useCallback((signal: AbortSignal) => port.detail(id, signal), [id, port]);
  const detail = useDetailRead(`warning:${id}`, read);
  const readConfig = useCallback(
    async (signal: AbortSignal) =>
      configurationPort ? configurationPort.warningConfiguration(id, signal) : null,
    [id, configurationPort],
  );
  const config = useDetailRead(`warning-config:${id}`, readConfig);
  const [pin, setPin] = useState<{ id: string; revision: number } | null>(
    revision === undefined ? null : { id, revision },
  );
  const [receipt, setReceipt] = useState<Acknowledgement | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (revision !== undefined && (pin?.id !== id || pin.revision !== revision))
      setPin({ id, revision });
    else if (detail.state.data && (!pin || pin.id !== id)) {
      const number = detail.state.data.warning.revision;
      setPin({ id, revision: number });
      if (typeof window !== 'undefined')
        window.history.replaceState(
          null,
          '',
          riskHref(`/warnings/${encodeURIComponent(id)}`, areaId, { revision: String(number) }),
        );
    }
  }, [id, revision, detail.state.data, pin, areaId]);
  const data = detail.state.status === 'READY' ? detail.state.data : null;
  const selectedNumber = revision ?? (pin?.id === id ? pin.revision : data?.warning.revision);
  const selected =
    data?.revisions.find((r) => r.revision === selectedNumber) ??
    (data && data.warning.revision === selectedNumber ? data.warning : null);
  const acknowledged = Boolean(
    selected?.acknowledged ||
    data?.acknowledgements.some((r) => r.revision === selectedNumber) ||
    (receipt?.warningId === id && receipt.revision === selectedNumber),
  );
  async function acknowledge() {
    if (!selected || busy || acknowledged || !data) return;
    const target = selected.revision;
    controller.current = new AbortController();
    setBusy(true);
    setFailure(null);
    try {
      const next = await port.acknowledge(id, target, controller.current.signal);
      if (alive.current) {
        setReceipt(next);
        detail.retry();
      }
    } catch (error) {
      if (alive.current) setFailure(detailFailure(error));
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  return (
    <section className="nm-vs04-detail" aria-label="观测预警详情">
      <DetailReadNotice state={detail.state} retry={detail.retry} label="预警详情" />
      {data && !selected && (
        <div role="status" className="nm-vs04-read">
          找不到第 {selectedNumber} 版预警。
          <a
            href={riskHref(`/warnings/${encodeURIComponent(id)}`, areaId, {
              revision: String(data.warning.revision),
            })}
          >
            查看最新修订
          </a>
        </div>
      )}
      {data && selected && (
        <div data-nm-detail data-nm-warning-id={id} data-nm-revision={selected.revision}>
          <div className="nm-vs04-hero">
            <span className="nm-vs04-tag nm-vs04-tag-warning">
              {selected.state === 'ACTIVE' ? '活动观测预警' : '历史观测预警'} · 第{' '}
              {selected.revision} 版
            </span>
            <h2>
              {selected.areaId} · {selected.hypothesis}
            </h2>
            <p>{selected.reason}</p>
            <p className="nm-vs04-muted">
              来自合成规则检测，尚未经现实事故核实。关联来源 {new Set(selected.sourceIds).size} 个 ·{' '}
              {selected.algorithmVersion}
            </p>
          </div>
          {data.warning.revision !== selected.revision && (
            <p className="nm-vs04-note">
              当前正在查看历史第 {selected.revision} 版，最新已为第 {data.warning.revision} 版（
              {data.warning.acknowledged ? '已读' : '未读'}）。
              <a
                href={riskHref(`/warnings/${encodeURIComponent(id)}`, areaId, {
                  revision: String(data.warning.revision),
                })}
              >
                查看最新修订 →
              </a>
            </p>
          )}
          <div className="nm-vs04-grid">
            <div className="nm-vs04-panel">
              <WarningRevisionEvidence
                data={data}
                revision={selected}
                configuration={config.state.status === 'READY' ? config.state.data : null}
              />
              {configurationPort && (
                <DetailReadNotice state={config.state} retry={config.retry} label="历史配置依据" />
              )}
              <div className="nm-vs04-section">
                <h3>预警变化时间线</h3>
                <p className="nm-vs04-muted">每一版均保存独立依据；选择修订后可直接刷新或分享。</p>
                <ol className="nm-vs04-timeline">
                  {[...data.revisions]
                    .sort((a, b) => b.revision - a.revision)
                    .map((r) => (
                      <li key={r.revision}>
                        <a
                          href={riskHref(`/warnings/${encodeURIComponent(id)}`, areaId, {
                            revision: String(r.revision),
                          })}
                        >
                          第 {r.revision} 版 · {r.state}
                        </a>
                        <p className="nm-vs04-muted">{r.updatedAt}</p>
                        <p>{r.reason}</p>
                        <small>
                          {r.acknowledged ? '已读本修订' : '未读本修订'} · episode {r.episode}
                        </small>
                      </li>
                    ))}
                </ol>
              </div>
            </div>
            <aside className="nm-vs04-panel">
              <h3>预警状态与操作</h3>
              <div className="nm-vs04-controls">
                <label>
                  查看预警修订
                  <select
                    aria-label="查看预警修订"
                    value={selected.revision}
                    onChange={(e) =>
                      window.location.assign(
                        riskHref(`/warnings/${encodeURIComponent(id)}`, areaId, {
                          revision: e.target.value,
                        }),
                      )
                    }
                  >
                    {data.revisions.map((r) => (
                      <option key={r.revision} value={r.revision}>
                        第 {r.revision} 版 · {r.state}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <Fields
                items={[
                  ['预警编号', id],
                  ['所属区域', selected.areaId],
                  ['本修订状态', selected.state],
                  ['规则版本', selected.algorithmVersion],
                  ['配置策略', selected.profileVersion],
                  ['创建时间', selected.createdAt],
                  ['修订时间', selected.updatedAt],
                  ['episode', selected.episode],
                  ['分类', selected.classification],
                ]}
              />
              <p data-nm-ack={acknowledged ? 'READ' : 'UNREAD'}>
                <strong>{acknowledged ? '已读本修订' : '未读本修订'}</strong>
              </p>
              <p className="nm-vs04-caution">
                标记已阅仅表示看过指定修订，不解除风险、不确认事故、不授权执行。
              </p>
              <button
                type="button"
                className="nm-vs04-primary"
                disabled={busy || acknowledged}
                onClick={() => {
                  void acknowledge();
                }}
              >
                {busy ? '提交已读回执…' : '确认已读本修订'}
              </button>
              {failure && <p role="status">已读提交失败：{failure}。可重试。</p>}
              {receipt?.warningId === id && (
                <p role="status">
                  已保存回执：第 {receipt.revision} 版 · {receipt.actor} · {receipt.readAt}
                </p>
              )}
              <details className="nm-vs04-section">
                <summary>独立已读记录</summary>
                {data.acknowledgements.map((ack) => (
                  <p key={`${ack.revision}/${ack.actor}`}>
                    第 {ack.revision} 版 · {ack.actor} · {ack.readAt}
                  </p>
                ))}
                {!data.acknowledgements.length && <p>暂无已读记录。</p>}
              </details>
              <details className="nm-vs04-section">
                <summary>局限、冲突与缺失</summary>
                <p>支持引用：{refs(selected.supportObservationRefs)}</p>
                <p>冲突引用：{refs(selected.conflictObservationRefs)}</p>
                <p>缺失来源：{refs(selected.missingSourceIds)}</p>
                <p>{refs(selected.limitations)}</p>
              </details>
            </aside>
          </div>
        </div>
      )}
    </section>
  );
}
